import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import { CallRouteUsage } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup.pb';
import {
  ReadResponse,
  CreateResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { useQueryClient } from 'react-query';
import { CallGroupApi, CallGroupTypes } from '@frontend/api-call-group';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useInvalidateQueries } from '@frontend/location-helpers';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Heading,
  ModalControlResponse,
  Tray,
  TextField,
  BannerNotification,
  TextLink,
  Button,
  useAlert,
  useModalControl,
  Modal,
  Text,
  Truncated,
  ModalControlModalProps,
  useFormField,
  MultiselectField,
} from '@frontend/design-system';
import { useCallGroupBatchedState, buildCallGroupUpdateV2Payload } from '../../hooks/useCallGroupBatchedState';
import { useCallGroupCacheUpdater } from '../../hooks/useCallGroupCacheUpdater';
import { queryKeys } from '../../query-keys';
import { logError } from '../../utils/loggers';
import { CallLegsWithModal } from '../call-group/call-legs-with-modal';

type Props = {
  addStepModalProps?: ModalControlModalProps;
  modalControls: ModalControlResponse;
  mountTarget: string;
  selectedCallGroup?: ReadResponse;
  stepContext?: {
    onCancelClick?: () => void;
    onClose?: () => void;
  };
};

export const CallGroupSettingTray = ({
  addStepModalProps,
  modalControls,
  mountTarget,
  selectedCallGroup,
  stepContext,
}: Props) => {
  const { t } = useTranslation('phone');
  const queryClient = useQueryClient();
  const callGroupId = selectedCallGroup?.callGroupId || '';
  const alert = useAlert();
  const invalidateQueries = useInvalidateQueries();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { modalProps: usageModalProps, triggerProps: usageTriggerProps } = useModalControl();
  const { selectedGroupId } = useAppScopeStore();

  const { usageData, updateCallRouteCaches } = useCallGroupCacheUpdater(callGroupId);

  const callRouteUsages = usageData.filter((usage) => 'callRouteUsage' in usage);

  const {
    callGroupState,
    setCallGroupState,
    initialCallGroupState,
    resetCallGroupState,
    form,
    nameField,
    hasUnsavedChanges,
    updateMaskPaths,
    handleRemoveDevice,
    handleRingChange,
    handleDevicesAssigned,
  } = useCallGroupBatchedState();

  const { mutate: updateCallGroupV2, isLoading: isUpdatingCallGroup } = useMutation(
    (payload: CallGroupTypes.UpdateV2Types['input']) => {
      return CallGroupApi.updateV2(payload);
    },
    {
      onSuccess: (data) => {
        // Calculate updated device information from the saved call group
        const deviceNames =
          data?.devices?.reduce<string[]>((acc, device) => {
            let name = '';
            if ('deskPhone' in device) {
              name = device.deskPhone.name;
            } else if ('softPhone' in device) {
              name = device.softPhone.name;
            } else if ('mobilePhone' in device) {
              name = device.mobilePhone.name;
            } else if ('forwardingNumber' in device) {
              name = device.forwardingNumber.name;
            }

            if (name) {
              acc.push(name);
            }
            return acc;
          }, []) || [];

        // Update call routes cache with new expansion data
        updateCallRouteCaches({
          deviceNames,
          deviceCount: deviceNames.length,
          callGroupName: data.name,
        });

        // Update the call group cache directly
        queryClient.setQueryData(queryKeys.callGroup(callGroupId), data);
        invalidateQueries(queryKeys.listCallGroups(settingsTenantLocation?.phoneTenantId ?? ''));
        invalidateQueries(queryKeys.callGroup(callGroupId));
        alert.success(t('Call Group updated successfully.'));
        resetCallGroupState(callGroupState);
        modalControls.closeModal();
      },
      onError: (error) => {
        alert.error(t('System failed to update Call Group. Please try again.'));
        logError('CallGroupSettings:updateCallGroupMutation | Failed to update call group', {
          data: {
            callGroupId: callGroupState.callGroupId,
            phoneTenantId: settingsTenantLocation?.phoneTenantId,
            error,
          },
        });
      },
    }
  );

  // Sync callGroupState with the data only when modal opens or callGroupId changes
  useEffect(() => {
    if (selectedCallGroup && modalControls.modalProps.show) {
      resetCallGroupState({
        name: selectedCallGroup.name ?? '',
        ringtone: selectedCallGroup.ringtone ?? '',
        locationIds: selectedCallGroup.locationIds ?? [],
        callGroupId: selectedCallGroup.callGroupId ?? '',
        tenantId: selectedCallGroup.tenantId ?? '',
        devices: selectedCallGroup.devices ?? [],
      });
    }
  }, [selectedCallGroup?.callGroupId, modalControls.modalProps.show, resetCallGroupState]);

  useEffect(() => {
    if (selectedCallGroup?.name) {
      form.seedValues({ name: selectedCallGroup.name });
    }
  }, [selectedCallGroup?.callGroupId, selectedCallGroup?.name, modalControls.modalProps.show]);

  const handleSave = useCallback(() => {
    if (!updateMaskPaths.length) {
      modalControls.closeModal();
      return;
    }

    updateCallGroupV2(buildCallGroupUpdateV2Payload(callGroupState, updateMaskPaths));
  }, [callGroupState, updateMaskPaths, updateCallGroupV2, modalControls]);

  const handleCancel = useCallback(() => {
    form.reset();
    setCallGroupState(initialCallGroupState);
    modalControls.closeModal();
  }, [form, initialCallGroupState, setCallGroupState, modalControls]);

  return (
    <Tray
      mountTarget={mountTarget}
      width='xlarge'
      zIndex={10001}
      css={css`
        padding: ${theme.spacing(4)};
        border-top: 1px solid ${theme.colors.neutral20};
        overflow-y: auto;
      `}
      {...modalControls.modalProps}
    >
      <Tray.Header>{t('Edit Call Group')}</Tray.Header>
      <Tray.Body>
        <Heading level={3} css={{ marginBottom: !!callRouteUsages?.length ? theme.spacing(1) : theme.spacing(2) }}>
          {t('Settings')}
        </Heading>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          {callRouteUsages?.length > 1 && (
            <BannerNotification
              status='info'
              message={
                <Trans t={t}>
                  This Call Group is being used in{' '}
                  <TextLink as='span' href='#' css={{ display: 'inline' }} onClick={usageTriggerProps.onClick}>
                    {t('{{count}} different step', { count: callRouteUsages.length })}
                  </TextLink>{' '}
                  in your Call Routes. Any changes you make here will be reflected in all of those places.
                </Trans>
              }
            />
          )}
          {selectedCallGroup && (
            <>
              <TextField
                {...nameField}
                label={t('Call Group Name')}
                name='name'
                css={{ maxWidth: theme.spacing(42) }}
              />

              <CallLegsWithModal
                callGroup={{ ...selectedCallGroup, ...callGroupState }}
                enableDeviceEditing={false}
                tableLayout='combined'
                removeIcon={() => <Icon name='remove2' color='subdued' />}
                onRemoveDevice={handleRemoveDevice}
                onRingChange={handleRingChange}
                onDevicesAssigned={handleDevicesAssigned}
              />

              {settingsTenantLocation?.locationType === 'unify' && !!selectedGroupId && (
                <>
                  <div css={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    <Heading level={3}>{t('Locations (Optional)')}</Heading>
                    <Text color='light'>{t('Label this Call Group with locations for reference and filtering.')}</Text>
                  </div>
                  <LocationsSelector callGroup={callGroupState} setCallGroup={setCallGroupState} />
                </>
              )}

              <div
                css={css`
                  display: flex;
                  gap: ${theme.spacing(2)};
                  justify-content: flex-end;
                `}
              >
                <Button size='large' variant='secondary' onClick={handleCancel}>
                  {t('Cancel')}
                </Button>
                <Button
                  size='large'
                  css={{ width: '62px' }}
                  variant='primary'
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges}
                  loading={isUpdatingCallGroup}
                  trackingId='phone::setting::call-route:save-callgroup-btn'
                >
                  {t('Save')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Tray.Body>

      <CallGroupUsageModal
        modalProps={usageModalProps}
        usageData={callRouteUsages || []}
        callGroupId={callGroupId}
        modalControls={modalControls}
        addStepModalProps={addStepModalProps}
        stepContext={stepContext}
      />
    </Tray>
  );
};

const CallGroupUsageModal = ({
  modalProps,
  usageData,
  callGroupId,
  modalControls,
  addStepModalProps,
  stepContext,
}: {
  modalProps: any;
  usageData: {
    callRouteUsage: CallRouteUsage;
  }[];
  callGroupId: string;
  modalControls: ModalControlResponse;
  addStepModalProps?: ModalControlModalProps;
  stepContext?: {
    onCancelClick?: () => void;
    onClose?: () => void;
  };
}) => {
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();

  return (
    <Modal {...modalProps} minWidth={600}>
      <Modal.Header onClose={modalProps.onClose}>{t('Call Group Usage')}</Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <Text>{t('This Call Group is currently assigned to the following Call Routes:')}</Text>
          <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {usageData.map(({ callRouteUsage: { callRouteId, callRouteName, usageCount } }) => (
              <div key={callRouteId} css={{ display: 'flex', gap: '4px', alignItems: 'center', width: '500px' }}>
                <div css={{ flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Truncated
                    as={TextLink}
                    textAlign='left'
                    onClick={() => {
                      modalProps.onClose();
                      modalControls.closeModal();
                      addStepModalProps?.onClose();
                      stepContext?.onCancelClick?.();
                      stepContext?.onClose?.();
                      navigate({
                        to: '/phone/call-routes/:id',
                        params: { id: callRouteId },
                        search: { callObjectId: callGroupId },
                      });
                    }}
                  >
                    {callRouteName}
                  </Truncated>
                </div>
                {usageCount !== undefined && usageCount > 1 && (
                  <span css={{ whiteSpace: 'nowrap', flexShrink: 0 }}>({usageCount} steps)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Actions showPrimary={false} secondaryLabel={t('Close')} onSecondaryClick={modalProps.onClose} />
    </Modal>
  );
};

const LocationsSelector = ({
  callGroup,
  setCallGroup,
}: {
  callGroup: CreateResponse;
  setCallGroup: Dispatch<SetStateAction<CreateResponse>>;
}) => {
  const { t } = useTranslation('phone');
  const field = useFormField({ type: 'multiselect', value: callGroup.locationIds }, [callGroup.callGroupId]);
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const options = settingsTenantLocation?.children?.map((item) => {
    return {
      locationId: item.locationId,
      name: item.name,
    };
  });

  useEffect(() => {
    setCallGroup((prevState) => ({
      ...prevState,
      locationIds: field.value,
    }));
  }, [field.value, setCallGroup]);

  return (
    <MultiselectField
      css={{ width: '537px' }}
      {...field}
      label={t('None Selected (All Locations)')}
      name='locations'
      helperText={t('Selecting none will auto-label all locations to this Call Group.')}
    >
      {options?.map((option) => (
        <MultiselectField.Option key={option.locationId} value={option.locationId ?? ''}>
          {option.name}
        </MultiselectField.Option>
      ))}
    </MultiselectField>
  );
};
